import { createWithEqualityFn } from 'zustand/traditional';

import { CollegeSearchFilters, IpedsResult, SortDirection } from '@common-types';

import { DESKTOP_PER_PAGE_OPTIONS } from '@constants';
import { REST_OF_US_FILTER, STATE_PUBLIC_COLLEGE_FILTER, SchoolListSortOption } from '@constants/stateSpotlight';
import type { SortByValues } from '@pages/Scholarships/components/SortBy';
import { trackEvent } from '@utils/googleAnalytics';

import { baseService } from '@services/base.service';

export interface SchoolSearchState extends CollegeSearchFilters {
  skip?: number;
  limit?: number;
  totalCount?: number;
  [key: string]: string[] | boolean | number | string | object | undefined;
}

interface CostFilter {
  min: number;
  max: number;
  step?: number;
}

interface ViewOptions {
  isLinearView?: boolean;
}

export type SchoolSortByValues = SortByValues<SchoolListSortOption>;

export const INITIAL_SCHOOL_SORT: SchoolSortByValues = {
  sort: SchoolListSortOption.DISTANCE,
  sortDirection: SortDirection.ASC,
};

interface CollegeSearchFiltersStore {
  filters: SchoolSearchState;
  sortByValues: SchoolSortByValues;
  setSortByValues: (sortByValues: SchoolSortByValues) => void;
  data: IpedsResult;
  viewOptions: ViewOptions;
  costFilter: CostFilter;
  isLoading: boolean;
  setViewOptions: (viewOptions: ViewOptions) => void;
  setSearchFilters: (searchFilters: SchoolSearchState) => void;
  removeSearchFilter: (searchFilter: keyof SchoolSearchState) => void;
  removeSearchFilterValue: (searchFilter: { key: keyof CollegeSearchFilters; value: unknown }) => void;
  resetSearchFilters: (searchFilters: SchoolSearchState) => void;
  getSchools: () => Promise<void>;
  getAverageCostLimit: () => Promise<void>;
}

const initialState = {
  filters: {} as SchoolSearchState,
  sortByValues: INITIAL_SCHOOL_SORT,
  viewOptions: {} as ViewOptions,
  data: {} as IpedsResult,
  costFilter: {} as CostFilter,
  isLoading: false,
};

export const useSchoolSearchFiltersStore = createWithEqualityFn<CollegeSearchFiltersStore>()(
  (set, get) => ({
    ...initialState,

    setViewOptions: (viewOptions: ViewOptions) => {
      set({ viewOptions: { ...viewOptions } });
    },

    setSortByValues: (sortByValues: SchoolSortByValues) => {
      set({ sortByValues });
    },

    setSearchFilters: (searchFilters: SchoolSearchState) => {
      set({
        filters: { ...get().filters, ...searchFilters },
      });
    },

    removeSearchFilter: (searchFilter: keyof SchoolSearchState) => {
      const filters = { ...get().filters };

      delete filters[searchFilter];

      set({
        filters,
      });
    },

    removeSearchFilterValue: (searchFilter: { key: keyof CollegeSearchFilters; value: unknown }) => {
      const filters = { ...get().filters };

      if (
        searchFilter.key === 'cost' ||
        searchFilter.key === 'keywords' ||
        searchFilter.key === 'hasScholarship' ||
        searchFilter.key === 'id' ||
        (searchFilter.key as string) === STATE_PUBLIC_COLLEGE_FILTER ||
        (searchFilter.key as string) === REST_OF_US_FILTER
      ) {
        delete filters[searchFilter.key as keyof SchoolSearchState];
      } else {
        const currentFilter = filters[searchFilter.key as keyof SchoolSearchState] as string[];

        const newValue = currentFilter.filter((item) => item !== searchFilter.value);

        if (newValue.length === 0) {
          delete filters[searchFilter.key as keyof SchoolSearchState];
        }

        filters[searchFilter.key as keyof SchoolSearchState] = newValue;
      }

      set({
        filters,
      });
    },

    resetSearchFilters: (searchFilters: SchoolSearchState) => {
      set({
        filters: { ...searchFilters },
      });
    },

    getSchools: async () => {
      set({ isLoading: true });
      const { filters } = get();

      const { cost, keywords, hasScholarship, skip, limit } = filters;
      const params: Record<string, string | number | boolean> = {
        limit: limit || DESKTOP_PER_PAGE_OPTIONS[0],
      };

      if (cost) {
        params.cost = cost as number;
      }

      if (hasScholarship) {
        params.hasScholarship = hasScholarship as boolean;
      }

      if (keywords) {
        params.keywords = encodeURIComponent(keywords as string);

        trackEvent({
          eventType: 'Keyword Search',
          props: {
            keywords: keywords as string,
          },
        });
      }

      if (skip) {
        params.skip = skip;
      }

      const arrayFields: (keyof CollegeSearchFilters)[] = [
        'states',
        'carnegieSizeSettings',
        'institutionTypes',
        'cipCodes',
        'awardLevels',
        'totalStudentsEnrolled',
        'locales',
        'specialty',
      ];

      arrayFields.forEach((filter) => {
        const filterValue = filters[filter as keyof SchoolSearchState] as string[];

        if (filterValue?.length) {
          params[filter as string] = filterValue.join(',');
        }
      });

      const data = await baseService.getAsync<IpedsResult>(`/`, { params });

      set({
        data,
        isLoading: false,
      });
    },

    getAverageCostLimit: async () => {
      if (get().costFilter.min && get().costFilter.max) return;

      let { min = 0, max = 0 } = await baseService.getAsync<{
        min: number;
        max: number;
      }>('/study-cost');

      min = Math.ceil(min / 1000) * 1000;
      max = Math.ceil(max / 1000) * 1000;

      set({
        costFilter: {
          min,
          max,
          step: 1000,
        },
      });
    },
  }),
  Object.is,
);
